/**
 * Invitation acceptance page — /invite/accept?token=...
 *
 * See docs/users-onboarding-billing-plan.md §4.2.
 *
 * Flow:
 *   1. Read token from URL param. If missing → error.
 *   2. If user isn't signed in yet, prompt them to sign in
 *      (with a return-to pointing back here so the token
 *      survives the WorkOS round-trip).
 *   3. Once signed in, POST /api/invitations/accept { token }.
 *   4. On success, navigate to /w/:slug/app/dashboard.
 *   5. On failure, render the specific reason (email mismatch is
 *      the common one — someone forwarded an invite that isn't
 *      for them).
 *
 * NOT gated by RequireAuth in the router — this page needs to
 * handle the "not signed in yet" case itself so it can preserve
 * the token across sign-in.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { req } from '../lib/api';

type State =
  | { kind: 'no_token' }
  | { kind: 'auth_loading' }
  | { kind: 'need_signin' }
  | { kind: 'redeeming' }
  | { kind: 'redeemed'; workspace_slug: string; role: string }
  | { kind: 'error'; message: string; reason?: string };

export function InviteAccept() {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>(() =>
    token ? { kind: 'auth_loading' } : { kind: 'no_token' }
  );
  // Prevent double-POST if React StrictMode double-renders in dev.
  const redeemingRef = useRef(false);

  // Where to send the user after sign-in — back to this page WITH
  // the token in the query string. Encoded so WorkOS preserves it.
  const returnTo = useMemo(() => {
    if (!token) return null;
    return `/invite/accept?token=${encodeURIComponent(token)}`;
  }, [token]);

  useEffect(() => {
    if (!token) return;
    if (loading) {
      setState({ kind: 'auth_loading' });
      return;
    }
    if (!user) {
      setState({ kind: 'need_signin' });
      return;
    }
    if (redeemingRef.current) return;
    redeemingRef.current = true;
    setState({ kind: 'redeeming' });
    (async () => {
      try {
        const body = await req<{
          status?: 'ok' | 'refused';
          workspace_slug?: string;
          role?: string;
          reason?: string;
          detail?: string;
        }>('/api/invitations/accept', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });
        if (body.status === 'ok' && body.workspace_slug) {
          setState({
            kind: 'redeemed',
            workspace_slug: body.workspace_slug,
            role: body.role ?? 'viewer',
          });
          // Small delay so the "You're in!" message renders before
          // we navigate — avoids a jarring flash of nothing.
          setTimeout(() => {
            navigate(`/w/${body.workspace_slug}/app/dashboard`, { replace: true });
          }, 1200);
        } else {
          setState({
            kind: 'error',
            message: body.detail ?? `Couldn't accept invitation.`,
            reason: body.reason,
          });
        }
      } catch (e) {
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : String(e),
        });
      }
    })();
  }, [token, loading, user, navigate]);

  // ---- Render -------------------------------------------------------

  return (
    <div style={shell}>
      <div style={card}>
        {state.kind === 'no_token' ? (
          <>
            <h1 style={h1}>Invitation link is missing</h1>
            <p style={p}>
              The link you followed doesn't have an invitation token. Ask
              whoever invited you to resend the link.
            </p>
          </>
        ) : null}

        {state.kind === 'auth_loading' ? (
          <>
            <h1 style={h1}>Checking your session…</h1>
            <p style={p}>One moment.</p>
          </>
        ) : null}

        {state.kind === 'need_signin' ? (
          <>
            <h1 style={h1}>Sign in to accept this invitation</h1>
            <p style={p}>
              You need to be signed in to join a workspace. Use the same
              email address the invitation was sent to.
            </p>
            <button
              type="button"
              onClick={() => {
                // Store the return-to path so post-auth we come back.
                // (The AuthProvider's signIn redirects to WorkOS; we
                // don't have a return-URL parameter through it yet,
                // so we stash in sessionStorage as a bridge. Improved
                // in a later pass via WorkOS `?return_to=`.)
                if (returnTo) {
                  try {
                    window.sessionStorage.setItem(
                      'aigovops:post-signin-redirect',
                      returnTo
                    );
                  } catch {
                    /* ignore */
                  }
                }
                signIn();
              }}
              style={btnPrimary}
            >
              Sign in with WorkOS
            </button>
          </>
        ) : null}

        {state.kind === 'redeeming' ? (
          <>
            <h1 style={h1}>Joining workspace…</h1>
            <p style={p}>One moment.</p>
          </>
        ) : null}

        {state.kind === 'redeemed' ? (
          <>
            <h1 style={{ ...h1, color: 'var(--success)' }}>You're in ✓</h1>
            <p style={p}>
              Joined <strong>{state.workspace_slug}</strong> as{' '}
              <strong>{state.role}</strong>. Taking you to the dashboard…
            </p>
          </>
        ) : null}

        {state.kind === 'error' ? (
          <>
            <h1 style={{ ...h1, color: 'var(--critical)' }}>Couldn't accept invitation</h1>
            <p style={p}>{errorCopy(state.reason, state.message)}</p>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <a href="/" style={btnGhost}>
                Back to home
              </a>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** Friendlier copy for the common refusal reasons. */
function errorCopy(reason: string | undefined, fallback: string): string {
  switch (reason) {
    case 'email_mismatch':
      return "This invitation is for a different email address. Ask the sender to reissue it to the address you're signed in with.";
    case 'expired':
      return 'This invitation link expired. Ask the sender to send a new one.';
    case 'revoked':
      return 'This invitation was revoked. Ask the sender to reissue it if you should still have access.';
    case 'already_used':
      return "This invitation has already been used. If it wasn't you, ask the workspace owner what's going on.";
    case 'already_member':
      return "You're already a member of this workspace. Nothing to do here.";
    case 'workspace_gone':
      return "The workspace this invitation was for no longer exists.";
    case 'not_found':
      return "This invitation link isn't valid. Double-check the URL or ask the sender to send a new one.";
    default:
      return fallback;
  }
}

// ---- Style tokens ---------------------------------------------------

const shell: React.CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  fontFamily: 'var(--font-ui, system-ui)',
  background: 'var(--surface)',
};
const card: React.CSSProperties = {
  maxWidth: 480,
  width: '100%',
  padding: '2rem',
  background: 'var(--surface)',
  border: '1px solid #ddd',
  borderRadius: '0.75rem',
};
const h1: React.CSSProperties = { fontSize: '1.5rem', marginTop: 0 };
const p: React.CSSProperties = {
  color: 'var(--text-dim)',
  marginBottom: '1rem',
};
const btnPrimary: React.CSSProperties = {
  appearance: 'none',
  padding: '0.7rem 1.1rem',
  borderRadius: '0.4rem',
  border: 0,
  background: 'var(--accent)',
  color: 'var(--text-inverse)',
  fontSize: '0.95rem',
  fontWeight: 500,
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.7rem 1.1rem',
  borderRadius: '0.4rem',
  border: '1px solid var(--border-strong)',
  background: 'var(--surface)',
  color: 'inherit',
  fontSize: '0.95rem',
  textDecoration: 'none',
};
